import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, X, Sparkles, MessageSquare, LogOut, User, Plus, MapPin, ChevronDown, Calendar } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { SearchBar } from '../Search/SearchBar';
import { SubscriptionModal } from '../Subscription/SubscriptionModal';
import NotificationBell from '../Notifications/NotificationBell';
import { supabase } from '../../lib/supabase';
import { isProfessional } from '../../lib/auth/authService';
import { Logo } from '../Logo';
import { TopNavigation } from './TopNavigation';
import { Avatar } from '../Profile/Avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { AddListingModal } from '../Listings/AddListingModal';
import { searchAddress, type GeocodeResult } from '../../lib/utils/geocoding';
import { useAuthProfile } from '../../contexts/AuthProfileContext';

const searchTypes = [
  { value: 'events', label: 'Events & Retreats', path: '/events' },
  { value: 'communities', label: 'Communities', path: '/communities' },
  { value: 'practitioners', label: 'Practitioners', path: '/practitioners' },
  { value: 'venues', label: 'Locations', path: '/venues' },
  { value: 'jobs', label: 'Jobs', path: '/jobs' },
];

interface HeaderProps {
  onLogin: () => void;
  isAuthenticated: boolean;
  onMenuClick: () => void;
}

export function Header({ onLogin, isAuthenticated, onMenuClick }: HeaderProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { profile } = useAuthProfile();
  const [showSearch, setShowSearch] = useState(false);
  const [showDesktopSearch, setShowDesktopSearch] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showAddListingModal, setShowAddListingModal] = useState(false);
  const [isPro, setIsPro] = useState(false);
  const [proCheckDone, setProCheckDone] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchType, setSearchType] = useState(searchTypes[0]);
  const [searchQuery, setSearchQuery] = useState('');
  const [locationQuery, setLocationQuery] = useState('');
  const [locationSuggestions, setLocationSuggestions] = useState<GeocodeResult[]>([]);
  const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);
  const [isLocationSearching, setIsLocationSearching] = useState(false);
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const locationSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const locationSuggestionsRef = useRef<HTMLDivElement>(null);

  const buildSearchParams = () => {
    const query = new URLSearchParams();
    if (searchQuery.trim()) query.set('q', searchQuery.trim());
    if (locationQuery.trim()) query.set('location', locationQuery.trim());
    return query.toString();
  };

  const handleSearch = () => {
    const params = buildSearchParams();
    navigate(params ? `${searchType.path}?${params}` : searchType.path);
    setShowDesktopSearch(false);
    setSearchQuery('');
    setLocationQuery('');
  };

  const handleUseCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((position) => {
        setLocationQuery(`${position.coords.latitude},${position.coords.longitude}`);
        setLocationSuggestions([]);
        setShowLocationSuggestions(false);
      });
    }
  };

  const handleLocationQueryChange = (value: string) => {
    setLocationQuery(value);

    if (locationSearchTimeoutRef.current) {
      clearTimeout(locationSearchTimeoutRef.current);
    }

    if (value.trim().length < 3) {
      setLocationSuggestions([]);
      setShowLocationSuggestions(false);
      setIsLocationSearching(false);
      return;
    }

    locationSearchTimeoutRef.current = setTimeout(async () => {
      setIsLocationSearching(true);
      const results = await searchAddress(value.trim());
      setLocationSuggestions(results);
      setShowLocationSuggestions(results.length > 0);
      setIsLocationSearching(false);
    }, 350);
  };

  const handleLocationSuggestionSelect = (suggestion: GeocodeResult) => {
    setLocationQuery(suggestion.display_name);
    setShowLocationSuggestions(false);
    setLocationSuggestions([]);
  };

  useEffect(() => {
    if (isAuthenticated) {
      checkProfessionalStatus();
    } else {
      setProCheckDone(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    return () => {
      if (locationSearchTimeoutRef.current) {
        clearTimeout(locationSearchTimeoutRef.current);
      }
    };
  }, []);

  // Close desktop search on Escape key and click outside
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showDesktopSearch) {
        setShowDesktopSearch(false);
      }
    };
    
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (showDesktopSearch && !target.closest('[data-search-dropdown]')) {
        setShowDesktopSearch(false);
      }
      if (showTypeDropdown && !target.closest('[data-type-dropdown]')) {
        setShowTypeDropdown(false);
      }
      if (showLocationSuggestions && locationSuggestionsRef.current && !locationSuggestionsRef.current.contains(target)) {
        setShowLocationSuggestions(false);
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDesktopSearch, showTypeDropdown, showLocationSuggestions]);

  const checkProfessionalStatus = async () => {
    const hasProfessionalAccess = await isProfessional();
    setIsPro(hasProfessionalAccess);
    setProCheckDone(true);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setIsDropdownOpen(false);
    navigate('/');
  };

  return (
    <header className="sticky top-0 z-50 bg-background border-b border-gray-200 pt-safe">
      <div className="max-w-page mx-auto px-4 sm:px-6 lg:px-10">
        <div className="flex items-center h-16">
          {/* Mobile Menu Button */}
          <button
            onClick={onMenuClick}
            className="lg:hidden relative p-3 hover:bg-[#8DA847]/10 rounded-xl group transition-colors"
            aria-label="Open menu"
          >
            <div className="w-6 h-5 flex flex-col justify-between">
              <span className="w-full h-0.5 bg-[#8DA847] rounded-full transition-transform origin-right group-hover:translate-y-0.5"></span>
              <span className="w-full h-0.5 bg-[#8DA847] rounded-full transition-opacity"></span>
              <span className="w-full h-0.5 bg-[#8DA847] rounded-full transition-transform origin-right group-hover:-translate-y-0.5"></span>
            </div>
            <div className="absolute inset-0 bg-[#8DA847]/5 rounded-xl scale-0 group-hover:scale-100 transition-transform" />
          </button>

          {/* Logo with Text - Desktop Only */}
          <Link to="/" className="hidden lg:flex items-center space-x-2">
            <Logo className="h-16 w-16 text-accent-text" />
            <span className="text-xl font-gelica font-bold" style={{ color: '#2F4F3E' }}>Mindful Family</span>
          </Link>

          {/* Search Box - Desktop Only */}
          <div className="hidden lg:flex flex-1 max-w-xl mx-6" ref={searchRef}>
            <div className="w-full flex items-center bg-white border border-gray-200 rounded-full shadow-sm hover:shadow-md transition-shadow">
              {/* Search Type Dropdown */}
              <div className="relative" data-search-dropdown data-type-dropdown>
                <button
                  onClick={() => setShowTypeDropdown(!showTypeDropdown)}
                  className="flex items-center px-4 py-3 text-sm font-medium text-gray-600 hover:text-gray-900 border-r border-gray-200"
                >
                  <span>{searchType.label}</span>
                  <ChevronDown className="ml-1 h-4 w-4 text-gray-400" />
                </button>
                {showTypeDropdown && (
                  <div className="absolute top-full left-0 mt-1 w-56 bg-white rounded-lg border border-gray-200 shadow-lg z-50">
                    {searchTypes.map((type) => (
                      <button
                        key={type.value}
                        onClick={() => {
                          setSearchType(type);
                          setShowTypeDropdown(false);
                          const params = buildSearchParams();
                          navigate(params ? `${type.path}?${params}` : type.path);
                        }}
                        className="w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg"
                      >
                        {type.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Location Input */}
              <div className="relative flex items-center flex-1" ref={locationSuggestionsRef}>
                <input
                  type="text"
                  placeholder="Address or location"
                  value={locationQuery}
                  onChange={(e) => handleLocationQueryChange(e.target.value)}
                  onFocus={() => {
                    if (locationSuggestions.length > 0) {
                      setShowLocationSuggestions(true);
                    }
                  }}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="w-full px-4 py-3 text-sm border-none outline-none bg-transparent"
                />
                {isLocationSearching && (
                  <div className="absolute right-12 text-xs text-gray-400">...</div>
                )}
                {showLocationSuggestions && locationSuggestions.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto">
                    {locationSuggestions.map((suggestion) => (
                      <button
                        key={suggestion.place_id}
                        type="button"
                        onClick={() => handleLocationSuggestionSelect(suggestion)}
                        className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                      >
                        {suggestion.display_name}
                      </button>
                    ))}
                  </div>
                )}
                <button
                  onClick={handleUseCurrentLocation}
                  className="mr-3 p-2 text-gray-400 hover:text-[#8DA847] transition-colors"
                  title="Use my location"
                >
                  <MapPin className="h-5 w-5" />
                </button>
              </div>

              {/* Search Button */}
              <button
                onClick={handleSearch}
                className="mr-1 my-1 px-4 py-2 bg-[#8DA847] text-white font-medium rounded-full hover:bg-[#8DA847]/90 transition-colors"
              >
                <Search className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Top Navigation - Desktop Only (after search) */}
          <div className="hidden lg:flex items-center">
            <TopNavigation />
          </div>

          {/* Right Side Icons - Desktop: Chat, Bell */}
          <div className="ml-auto flex items-center space-x-3 relative">

          {/* Auth/Upgrade/Notification Buttons */}
          {isAuthenticated ? (
            <>
              {/* Message Icon - Desktop Only */}
              <Link
                to="/chat"
                className="hidden lg:flex items-center justify-center p-2 text-content/60 hover:text-content hover:bg-accent-base/10 rounded-full transition-all"
                title="Messages"
              >
                <MessageSquare className="h-5 w-5" />
              </Link>
              
              {/* Mobile Search Button */}
              <button
                onClick={() => setShowSearch(true)}
                className="lg:hidden p-2 text-accent-text hover:bg-accent-base/20 rounded-full"
                title="Search"
              >
                <Search className="h-5 w-5" />
              </button>
              
              <NotificationBell />
              
              {/* User Avatar with Dropdown - Desktop Only */}
              {profile?.username && (
                <div
                  className="hidden lg:block relative"
                  onMouseEnter={() => setIsDropdownOpen(true)}
                  onMouseLeave={() => setIsDropdownOpen(false)}
                >
                  {/* Clickable Avatar - Always navigates to profile */}
                  <Link
                    to={`/profile/${profile.username}`}
                    className="block relative z-10 cursor-pointer"
                    onClick={(e) => {
                      // Always navigate, close dropdown if open
                      setIsDropdownOpen(false);
                    }}
                  >
                    <Avatar
                      url={profile.avatar_url}
                      username={profile.username}
                      size="sm"
                      disableLink={true}
                    />
                  </Link>

                  {/* Dropdown Menu - Opens on hover, positioned below avatar */}
                  {isDropdownOpen && (
                    <div
                      className="absolute right-0 top-full pt-2 w-48 z-50"
                    >
                      <div className="bg-background rounded-lg border border-border shadow-lg">
                        <div className="py-1">
                          <Link
                            to={`/profile/${profile.username}`}
                            className="flex items-center px-3 py-2 text-sm hover:bg-accent-base/10 cursor-pointer"
                            onClick={() => setIsDropdownOpen(false)}
                          >
                            <User className="h-4 w-4 mr-2" />
                            View Profile
                          </Link>
                          {isPro && (
                            <button
                              onClick={() => {
                                setShowAddListingModal(true);
                                setIsDropdownOpen(false);
                              }}
                              className="w-full flex items-center px-3 py-2 text-sm hover:bg-accent-base/10 cursor-pointer text-left"
                            >
                              <Plus className="h-4 w-4 mr-2" />
                              Add Offering
                            </button>
                          )}
                          <Link
                            to={`/profile/${profile.username}/bookings-hub`}
                            className="flex items-center px-3 py-2 text-sm hover:bg-accent-base/10 cursor-pointer"
                            onClick={() => setIsDropdownOpen(false)}
                          >
                            <Calendar className="h-4 w-4 mr-2" />
                            Bookings & settings
                          </Link>
                          <div className="h-px bg-border my-1" />
                          <button
                            onClick={() => {
                              handleLogout();
                              setIsDropdownOpen(false);
                            }}
                            className="w-full flex items-center px-3 py-2 text-sm hover:bg-accent-base/10 cursor-pointer text-left text-red-600"
                          >
                            <LogOut className="h-4 w-4 mr-2" />
                            Log Out
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {proCheckDone && !isPro && (
                <>
                  {/* Desktop Upgrade Button */}
                  <button
                    onClick={() => setShowUpgradeModal(true)}
                    className="hidden lg:inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-accent-text rounded-md hover:bg-accent-text/90"
                  >
                    <Sparkles className="h-4 w-4 mr-2" />
                    Upgrade to Pro
                  </button>
                  {/* Mobile Upgrade Button */}
                  <button
                    onClick={() => setShowUpgradeModal(true)}
                    className="lg:hidden inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-accent-text rounded-md hover:bg-accent-text/90"
                  >
                    <Sparkles className="h-4 w-4 mr-1" />
                    Upgrade
                  </button>
                </>
              )}
            </>
          ) : (
            <>
              {/* Desktop Sign In Button */}
              <button
                onClick={onLogin}
                className="hidden lg:inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-accent-text rounded-full hover:bg-accent-text/90 transition-colors whitespace-nowrap"
              >
                {t('common.signIn')}
              </button>
              {/* Mobile Sign In Button */}
              <button
                onClick={onLogin}
                className="lg:hidden inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-accent-text rounded-full hover:bg-accent-text/90 transition-colors whitespace-nowrap"
              >
                {t('common.signIn')}
              </button>
            </>
          )}
          </div>
        </div>

        {/* Mobile Search Overlay */}
        {showSearch && (
          <div className="fixed inset-0 z-50 bg-background">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-content">Search</h2>
              <button
                onClick={() => setShowSearch(false)}
                className="p-2 text-content/60 hover:text-content"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="p-4">
              <SearchBar onResultClick={() => setShowSearch(false)} />
            </div>
          </div>
        )}

        {/* Subscription Modal */}
        {showUpgradeModal && (
          <SubscriptionModal
            onClose={() => setShowUpgradeModal(false)}
            onSuccess={() => {
              setShowUpgradeModal(false);
              checkProfessionalStatus();
            }}
          />
        )}

        {/* Add Listing Modal */}
        {showAddListingModal && (
          <AddListingModal
            onClose={() => setShowAddListingModal(false)}
            onSuccess={() => {
              setShowAddListingModal(false);
            }}
          />
        )}
      </div>
    </header>
  );
}